import type { AiFeatureId, AiFeatureMeta } from './features';
import type { AiReasoningEffort, AiVerbosity } from './models';
import { AI_MODEL_IDS, AI_REASONING_EFFORTS, AI_VERBOSITIES } from './models';
import type { AiRunUsage, AiTimestampLike } from './runs';

export const AI_USAGE_STATS_COLLECTION = 'aiUsageStats';
export const AI_USAGE_STATS_MIN_SAMPLES = 3;
export const AI_USAGE_STATS_WINDOW_DAYS = 90;

/** One finished run, reduced to what the aggregation needs. */
export interface AiUsageSample {
  model: string;
  effort: AiReasoningEffort;
  verbosity: AiVerbosity;
  promptVersion: number;
  inputChars: number | null;
  usage: AiRunUsage;
  durationMs: number;
  costUsd: number | null;
}

/** Linear fit `input_tokens ≈ fixed_tokens + tokens_per_char × input_chars`; model-independent. */
export interface AiInputTokenFit {
  fixed_tokens: number;
  tokens_per_char: number;
  sample_size: number;
}

export interface AiUsageComboStats {
  model: string;
  effort: AiReasoningEffort;
  verbosity: AiVerbosity;
  /** `all-versions` when the current prompt version has too few runs and older ones were pooled in. */
  scope: 'current' | 'all-versions';
  sample_size: number;
  median_input_tokens: number;
  median_output_tokens: number;
  median_reasoning_tokens: number;
  median_cached_ratio: number;
  median_duration_ms: number;
  p90_duration_ms: number;
  median_cost_usd: number | null;
}

/** Per-model generation speed, used to estimate time for settings that have no runs yet. */
export interface AiModelSpeedStats {
  model: string;
  sample_size: number;
  median_ms_per_output_token: number;
}

export interface AiUsageStats {
  feature: AiFeatureId;
  prompt_version: number;
  window_days: number;
  sample_size: number;
  input: AiInputTokenFit | null;
  combos: AiUsageComboStats[];
  models: AiModelSpeedStats[];
}

export interface AiUsageStatsDoc<TTimestamp = AiTimestampLike> extends AiUsageStats {
  computed_at: TTimestamp;
}

export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

/** Nearest-rank percentile; `sorted` must be ascending. */
export function percentile(sorted: number[], fraction: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(sorted.length - 1, Math.floor(sorted.length * fraction));
  return sorted[index];
}

const DEFAULT_TOKENS_PER_CHAR = 0.25;
const MIN_TOKENS_PER_CHAR = 0.1;
const MAX_TOKENS_PER_CHAR = 1;

export function fitInputTokens(points: { inputChars: number; inputTokens: number }[]): AiInputTokenFit | null {
  const n = points.length;
  if (n < AI_USAGE_STATS_MIN_SAMPLES) return null;

  const meanX = points.reduce((sum, p) => sum + p.inputChars, 0) / n;
  const meanY = points.reduce((sum, p) => sum + p.inputTokens, 0) / n;
  let sxx = 0;
  let sxy = 0;
  for (const p of points) {
    sxx += (p.inputChars - meanX) ** 2;
    sxy += (p.inputChars - meanX) * (p.inputTokens - meanY);
  }

  const slope = sxx > 0 ? sxy / sxx : Number.NaN;
  const intercept = meanY - slope * meanX;
  if (Number.isFinite(slope) && slope >= MIN_TOKENS_PER_CHAR && slope <= MAX_TOKENS_PER_CHAR && intercept >= 0) {
    return { fixed_tokens: Math.round(intercept), tokens_per_char: Number(slope.toFixed(4)), sample_size: n };
  }

  // Too little spread in input size for a trustworthy slope: keep chars/4 and calibrate the constant.
  const residual = median(points.map((p) => p.inputTokens - p.inputChars * DEFAULT_TOKENS_PER_CHAR));
  return { fixed_tokens: Math.max(0, Math.round(residual)), tokens_per_char: DEFAULT_TOKENS_PER_CHAR, sample_size: n };
}

function toFitPoints(samples: AiUsageSample[]): { inputChars: number; inputTokens: number }[] {
  return samples
    .filter((sample) => sample.inputChars !== null && sample.usage.input_tokens > 0)
    .map((sample) => ({ inputChars: sample.inputChars as number, inputTokens: sample.usage.input_tokens }));
}

function comboKey(sample: Pick<AiUsageSample, 'model' | 'effort' | 'verbosity'>): string {
  return `${sample.model}|${sample.effort}|${sample.verbosity}`;
}

function summarizeCombo(samples: AiUsageSample[], scope: AiUsageComboStats['scope']): AiUsageComboStats {
  const [first] = samples;
  const durations = samples.map((s) => s.durationMs).sort((a, b) => a - b);
  const costs = samples.map((s) => s.costUsd).filter((cost): cost is number => cost !== null);

  return {
    model: first.model,
    effort: first.effort,
    verbosity: first.verbosity,
    scope,
    sample_size: samples.length,
    median_input_tokens: Math.round(median(samples.map((s) => s.usage.input_tokens))),
    median_output_tokens: Math.round(median(samples.map((s) => s.usage.output_tokens))),
    median_reasoning_tokens: Math.round(median(samples.map((s) => s.usage.reasoning_tokens))),
    median_cached_ratio: Number(
      median(samples.map((s) => (s.usage.input_tokens > 0 ? s.usage.cached_input_tokens / s.usage.input_tokens : 0))).toFixed(3),
    ),
    median_duration_ms: Math.round(median(durations)),
    p90_duration_ms: percentile(durations, 0.9),
    median_cost_usd: costs.length > 0 ? Number(median(costs).toFixed(6)) : null,
  };
}

function orderIndex(list: readonly string[], value: string): number {
  const index = list.indexOf(value);
  return index === -1 ? list.length : index;
}

/**
 * Aggregates finished runs of one feature. Runs on the current prompt version are preferred; older
 * versions are pooled in only where the current one has too few runs to be meaningful.
 */
export function buildUsageStats(feature: AiFeatureMeta, samples: AiUsageSample[]): AiUsageStats {
  const isCurrent = (sample: AiUsageSample) => sample.promptVersion === feature.promptVersion;
  const current = samples.filter(isCurrent);

  const groups = new Map<string, AiUsageSample[]>();
  for (const sample of samples) {
    const key = comboKey(sample);
    groups.set(key, [...(groups.get(key) ?? []), sample]);
  }

  const combos = [...groups.values()]
    .map((group) => {
      const currentGroup = group.filter(isCurrent);
      return currentGroup.length >= AI_USAGE_STATS_MIN_SAMPLES || currentGroup.length === group.length
        ? summarizeCombo(currentGroup, 'current')
        : summarizeCombo(group, 'all-versions');
    })
    .sort(
      (a, b) =>
        orderIndex(AI_MODEL_IDS, a.model) - orderIndex(AI_MODEL_IDS, b.model) ||
        orderIndex(AI_REASONING_EFFORTS, a.effort) - orderIndex(AI_REASONING_EFFORTS, b.effort) ||
        orderIndex(AI_VERBOSITIES, a.verbosity) - orderIndex(AI_VERBOSITIES, b.verbosity),
    );

  const speedSource = current.length >= AI_USAGE_STATS_MIN_SAMPLES ? current : samples;
  const byModel = new Map<string, number[]>();
  for (const sample of speedSource) {
    if (sample.usage.output_tokens <= 0) continue;
    byModel.set(sample.model, [...(byModel.get(sample.model) ?? []), sample.durationMs / sample.usage.output_tokens]);
  }
  const models = [...byModel.entries()]
    .map(([model, ratios]) => ({ model, sample_size: ratios.length, median_ms_per_output_token: Number(median(ratios).toFixed(3)) }))
    .sort((a, b) => orderIndex(AI_MODEL_IDS, a.model) - orderIndex(AI_MODEL_IDS, b.model));

  return {
    feature: feature.id,
    prompt_version: feature.promptVersion,
    window_days: AI_USAGE_STATS_WINDOW_DAYS,
    sample_size: samples.length,
    input: fitInputTokens(toFitPoints(current)) ?? fitInputTokens(toFitPoints(samples)),
    combos,
    models,
  };
}
